import React, { useEffect, useMemo, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { GridColDef, GridApi, GridRenderCellParams } from '@mui/x-data-grid-pro'
import { useNewTranslation } from 'hooks/index'
import { selectCampaign, selectRegistrationsList, selectRequestsAreLoading, selectSubmissionsList } from 'state'
import {
    MAX_PAGE_SIZE,
    selectActivePageNum,
    selectSelectedCampaignUuid,
    selectSearchTerm,
    REGISTRATION_VIEW_ENUM,
    selectFilteringParams,
    REGISTRATION_STATUS,
    selectIsSelectedCampaignDeadlineOver,
    MASS_REGISTRATION_ACTION_NAME_ENUM,
    selectSearchablePlatforms,
    selectReviewingCampaignCountry,
    selectSelectedSort,
} from 'state/review'
import {
    SUBMISSION_VIEW_ENUM,
    clearSubmissionsList,
    getDropoutsList,
    getRejectedList,
    exportSubmissions,
} from 'state/submissions'
import { STATE_KEYS } from 'state/requests'
import PartiTable, { PartiTableProps } from 'components/Shared/MuiTable/PartiTable'
import {
    WaitListTableRows,
    CompletedTimeTableRows,
    RejectedTableRows,
    DeclinedRegistrationResultsTableRows,
    DropoutsTableRows,
    RegistrationResultTableRows,
} from './constants'
import { getColumns as getDropoutColumns, getRows as getDropoutRows } from './DropoutsTableDef'
import { getColumns as getRejectedColumns, getRows as getRejectedRows } from './RejectedTableDef'
import {
    getColumns as getDeclinedRegistrationResultsColumns,
    getRows as getDeclinedRegistrationResultsRows,
} from './DeclinedRegistrationResultsTableDef'
import {
    getColumns as getRegistrationResultsColumns,
    getRows as getRegistrationResultsRows,
} from './RegistrationResultsTableDef'
import { getColumns as getWaitlistColumns, getRows as getWaitlistRows } from './WaitlistTableDef'

import { getColumns as getCompletedColumns, getRows as getCompletedRows } from './CompletedTimeTableDef'
import ExportModal from 'components/Submissions/Review/common/ExportModal'
import {
    clearRegistrationsList,
    exportRegistrationsList,
    getRegistrationsList,
    selectRegistrationForm,
    massRegistration,
    selectRegistrationRemainingSlots,
    selectViewedRows,
    BLOCK_TYPE,
    updateViewedRows,
    syncViewedRows,
    highlightRegistration,
    selectHighlightedRegistrations,
} from 'state/registrations'
import { StringCell } from 'components/Shared/MuiTable/Cell/StringCell'
import { UrlCell } from 'components/Shared/MuiTable/Cell/UrlCell'
import _ from 'lodash'
import { MediaCell } from 'components/Shared/MuiTable/Cell/MediaCell'
import {
    requestProfileOptions,
    selectOccupationOptions,
    requestLocationOptions,
    selectCityTypesByReviewingCampaignCountry,
} from 'state/constants'
import { usePrevious } from 'hooks'
import { selectTasks } from 'state/tasks'
import ListApprovalModal from 'components/Submissions/Review/Registrations/ListApprovalModal'
import { selectPartiposterProfiles } from 'state/partiposter'
import { getCampaignSummary } from 'state/campaigns'
import { GLOBAL_MODAL_TYPE, openModalWithProps, ActionModalProps } from 'state/constants'
import ActionModal from 'components/Submissions/Review/Submissions/WaitlistTable/ActionModal'
import { usePermission } from 'hooks/permissions'
import { CampaignSubmission } from 'components/Shared/Permission/enums'
import { CAMPAIGN_STATUS_ENUM } from 'state/campaigns'
import { RequestExportSubmissionsPayload } from 'services/api/submissions'
import { REGISTRATION_STATUS_ENUM } from 'services/api/registrations'
import { getCampaignFollowerRequirements } from 'components/utils/campaign'
import { clearSubmissionFilters } from 'state/filter'

interface SimepleTableProps {
    viewType: SUBMISSION_VIEW_ENUM | REGISTRATION_VIEW_ENUM
    tableHeight?: string
}

const SimpleTableWrapper: React.FC<SimepleTableProps> = ({ viewType, tableHeight }) => {
    const [newT] = useNewTranslation()
    const dispatch = useDispatch()
    const submissions = useSelector(selectSubmissionsList)
    const registrations = useSelector(selectRegistrationsList)
    const campaignUuid = useSelector(selectSelectedCampaignUuid)
    const campaign = useSelector(selectCampaign(campaignUuid as string))
    const isCampaignLive = Boolean(campaign?.status === CAMPAIGN_STATUS_ENUM.LIVE)
    const isSelectedCampaignDeadlineOver = useSelector(selectIsSelectedCampaignDeadlineOver)
    const tasks = useSelector(selectTasks)
    const dropoutsLoading = useSelector(selectRequestsAreLoading([STATE_KEYS.DropoutsList]))
    const rejectedLoading = useSelector(selectRequestsAreLoading([STATE_KEYS.RejectedList]))
    const registrationResultsLoading = useSelector(selectRequestsAreLoading([STATE_KEYS.RegistrationResultsList]))
    const submissionLoading = useSelector(selectRequestsAreLoading([STATE_KEYS.SubmissionsList]))
    const isRegistrationExportRequesting = useSelector(selectRequestsAreLoading([STATE_KEYS.ExportRegistrationResults]))
    const isSubmissionExportRequesting = useSelector(selectRequestsAreLoading([STATE_KEYS.ExportSubmissions]))
    const activePageNum = useSelector(selectActivePageNum)
    const searchTerm = useSelector(selectSearchTerm)
    const occupationOptions = useSelector(selectOccupationOptions)
    const profiles = useSelector(selectPartiposterProfiles)
    const registrationForm = useSelector(selectRegistrationForm)
    const interestRegistrationRemainingSlots = useSelector(selectRegistrationRemainingSlots)
    const filteringParams = useSelector(selectFilteringParams)
    const prevFilteringParams = usePrevious(filteringParams)
    const searchablePlatforms = useSelector(selectSearchablePlatforms)
    const sortQuery = useSelector(selectSelectedSort)
    const canApproveRegistrationAndManageWaitlist = usePermission(
        CampaignSubmission.ApproveRegistrationAndManageWaitList,
    )
    const canMoveRegistrationToPending = usePermission(CampaignSubmission.MoveRegistrationToPendingApproval)
    const canManageTag = usePermission(CampaignSubmission.ManageTag)

    const cityTypes = useSelector(selectCityTypesByReviewingCampaignCountry)
    const rank1CityType = cityTypes.find(city => city.rank === 1)
    const idName = rank1CityType ? rank1CityType.id_name : ''
    const reviewingCampaignCountry = useSelector(selectReviewingCampaignCountry)
    const viewedRows = useSelector(selectViewedRows)
    const highlightedRegistrations = useSelector(selectHighlightedRegistrations)

    useEffect(() => {
        return () => {
            dispatch(clearSubmissionFilters())
        }
    }, [dispatch, clearSubmissionFilters])

    useEffect(() => {
        if (reviewingCampaignCountry && idName) {
            dispatch(
                requestLocationOptions({
                    country: reviewingCampaignCountry,
                    type_id_name: idName,
                    parent: undefined,
                }),
            )
        }
    }, [reviewingCampaignCountry, idName])

    const memoizedFilteringParams = useMemo(() => {
        return filteringParams
    }, [_.isEqual(filteringParams, prevFilteringParams)])

    const registrationFormBlocksById = useMemo(() => {
        return registrationForm && registrationForm.blocks ? _.keyBy(registrationForm.blocks, 'uuid') : {}
    }, [registrationForm])

    const updateViewedRow = useCallback(
        (id: string) => {
            if (
                campaignUuid &&
                isCampaignLive &&
                viewType === REGISTRATION_VIEW_ENUM.PENDING &&
                canApproveRegistrationAndManageWaitlist
            ) {
                dispatch(updateViewedRows({ rowId: id }))
                if (!viewedRows.includes(id)) {
                    dispatch(syncViewedRows({ campaignUuid: campaignUuid, registrationId: id }))
                }
            }
        },
        [viewType, canApproveRegistrationAndManageWaitlist, isCampaignLive, campaignUuid, viewedRows.length],
    )

    const handleHighlightRegistration = useCallback(
        (registrationUuid: string, highlighted: boolean) => {
            if (
                campaignUuid &&
                isCampaignLive &&
                viewType === REGISTRATION_VIEW_ENUM.PENDING &&
                canApproveRegistrationAndManageWaitlist
            ) {
                dispatch(
                    highlightRegistration({
                        campaignUuid,
                        registrationUuid,
                        highlighted: !highlighted, // if highlighted is true, then toggle to false and vice versa
                    }),
                )
            }
        },
        [
            viewType,
            canApproveRegistrationAndManageWaitlist,
            isCampaignLive,
            campaignUuid,
            highlightedRegistrations.length,
        ],
    )

    const dynamicCols = useMemo<GridColDef[]>(() => {
        if (registrationForm && registrationForm.blocks) {
            return registrationForm.blocks
                .filter(block => ![BLOCK_TYPE.TEXT, BLOCK_TYPE.PHOTOS].includes(block.type))
                .map((block): GridColDef => {
                    const field = `${block.type}+${block.uuid}`
                    const headerName = block.title
                    switch (block.type as Exclude<BLOCK_TYPE, BLOCK_TYPE.TEXT | BLOCK_TYPE.PHOTOS>) {
                        case BLOCK_TYPE.QUESTION:
                            return {
                                field,
                                headerName,
                                width: 200,
                                renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                                sortable: false,
                            }
                        case BLOCK_TYPE.TEXT_SUBMISSION:
                            return {
                                field,
                                headerName,
                                width: 200,
                                renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                                sortable: false,
                                filterable: false,
                            }
                        case BLOCK_TYPE.NUMBER_SUBMISSION:
                            return {
                                field,
                                headerName,
                                width: 120,
                                type: 'number',
                                align: 'left',
                                headerAlign: 'left',
                                renderCell: (props: GridRenderCellParams) => <StringCell {...props} />,
                                sortable: false,
                                filterable: true,
                            }
                        case BLOCK_TYPE.URL_SUBMISSION:
                            return {
                                field,
                                headerName,
                                width: 200,
                                renderCell: function Url(params: GridRenderCellParams): React.ReactElement {
                                    const handleUpdateViewed = () => updateViewedRow(params.row.uuid)
                                    return <UrlCell {...params} onClick={handleUpdateViewed} />
                                },
                                sortable: false,
                                filterable: false,
                            }
                        case BLOCK_TYPE.PHOTO_SUBMISSION:
                            return {
                                field,
                                headerName,
                                width: 112,
                                renderCell: function Url(params: GridRenderCellParams): React.ReactElement {
                                    const handleUpdateViewed = () => updateViewedRow(params.row.uuid)
                                    return <MediaCell {...params} onClick={handleUpdateViewed} />
                                },
                                sortable: false,
                                filterable: false,
                            }
                    }
                })
        } else {
            return []
        }
    }, [registrationForm, registrationFormBlocksById, canApproveRegistrationAndManageWaitlist])

    const TABLE_TITLE = {
        [SUBMISSION_VIEW_ENUM.DROPOUTS]: newT('submission.exportList.partipostDropoutsTableTitle'),
        [SUBMISSION_VIEW_ENUM.REJECTED]: newT('submission.exportList.partipostRejectedTableTitle'),
        [REGISTRATION_VIEW_ENUM.DROPOUTS]: newT('submission.exportList.partipostDropoutsRegistrationTableTitle'),
        [REGISTRATION_VIEW_ENUM.REJECTED]: newT('submission.exportList.partipostRejectedRegistrationTableTitle'),
        [REGISTRATION_VIEW_ENUM.PENDING]: `${newT('taskType.registration')} - ${newT(
            'statusLabel.pending',
            'Pending',
        )}`,
        [REGISTRATION_VIEW_ENUM.WAITLISTED]: `${newT('taskType.registration')} - ${newT(
            'statusLabel.waitlist',
            'Waitlist',
        )}`,
        [REGISTRATION_VIEW_ENUM.AWAITING_RESPONSE]: `${newT('taskType.registration')} - ${newT(
            'statusLabel.awaitingResponse',
            'Awaiting Response',
        )}`,
        [REGISTRATION_VIEW_ENUM.ACCEPTED_INVITES]: `${newT('taskType.registration')} - ${newT(
            'statusLabel.accepted',
            'Accepted Invites',
        )}`,
        [REGISTRATION_VIEW_ENUM.JOINED_CAMPAIGN]: `${newT('taskType.registration')} - ${newT(
            'statusLabel.joinedCampaign',
            'Joined Campaign',
        )}`,
        [REGISTRATION_VIEW_ENUM.DECLINED_INVITES]: `${newT('taskType.registration')} - ${newT(
            'statusLabel.declined',
            'Declined Invites',
        )}`,
    }

    useEffect(() => {
        if (campaign) dispatch(requestProfileOptions({ country: campaign.country }))
    }, [campaign])

    const refreshList = useCallback(() => {
        const offset: number = activePageNum * MAX_PAGE_SIZE
        if (campaignUuid) {
            switch (viewType) {
                case REGISTRATION_VIEW_ENUM.DROPOUTS:
                    dispatch(
                        getDropoutsList({
                            type: 'registration',
                            campaignUuid,
                            offset,
                            keywords: searchTerm,
                        }),
                    )
                    break
                case SUBMISSION_VIEW_ENUM.DROPOUTS:
                    dispatch(
                        getDropoutsList({
                            type: 'submission',
                            campaignUuid,
                            offset,
                            keywords: searchTerm,
                        }),
                    )
                    break
                case REGISTRATION_VIEW_ENUM.REJECTED:
                    dispatch(
                        getRejectedList({
                            type: 'registration',
                            campaignUuid,
                            offset,
                            keywords: searchTerm,
                        }),
                    )
                    break
                case SUBMISSION_VIEW_ENUM.REJECTED:
                    dispatch(
                        getRejectedList({
                            type: 'submission',
                            campaignUuid,
                            offset,
                            keywords: searchTerm,
                        }),
                    )
                    break
                case REGISTRATION_VIEW_ENUM.WAITLISTED:
                    dispatch(
                        getRegistrationsList({
                            campaignUuid,
                            status: REGISTRATION_STATUS[viewType],
                            offset,
                            keywords: searchTerm,
                            is_priority: false,
                            ordering: sortQuery ? sortQuery : undefined,
                            filteringParams: memoizedFilteringParams,
                        }),
                    )
                    break
                case REGISTRATION_VIEW_ENUM.PENDING:
                case REGISTRATION_VIEW_ENUM.AWAITING_RESPONSE:
                case REGISTRATION_VIEW_ENUM.ACCEPTED_INVITES:
                case REGISTRATION_VIEW_ENUM.JOINED_CAMPAIGN:
                case REGISTRATION_VIEW_ENUM.DECLINED_INVITES:
                    dispatch(
                        getRegistrationsList({
                            campaignUuid,
                            status: REGISTRATION_STATUS[viewType],
                            offset,
                            keywords: searchTerm,
                            ordering: sortQuery ? sortQuery : undefined,
                            filteringParams: memoizedFilteringParams,
                        }),
                    )
                    break
            }
        }
    }, [campaignUuid, activePageNum, searchTerm, memoizedFilteringParams, viewType, sortQuery])

    useEffect(() => {
        refreshList()

        return () => {
            dispatch(clearSubmissionsList())
            dispatch(clearRegistrationsList())
        }
    }, [refreshList])

    const refreshInterestRegistrationList = useCallback(() => {
        dispatch(clearRegistrationsList())
        refreshList()
        if (campaignUuid) {
            dispatch(getCampaignSummary({ uuid: campaignUuid }))
        }
    }, [clearRegistrationsList, refreshList, campaignUuid])

    const handleClickExport = (viewType: SUBMISSION_VIEW_ENUM | REGISTRATION_VIEW_ENUM) => {
        if (!campaign || !campaign.uuid) return
        if (
            [
                SUBMISSION_VIEW_ENUM.REJECTED,
                SUBMISSION_VIEW_ENUM.DROPOUTS,
                REGISTRATION_VIEW_ENUM.REJECTED,
                REGISTRATION_VIEW_ENUM.DROPOUTS,
            ].includes(viewType)
        ) {
            return dispatch(
                exportSubmissions({
                    uuid: campaign.uuid,
                    viewType: viewType as RequestExportSubmissionsPayload['viewType'],
                }),
            )
        }
        if (!Object.keys(REGISTRATION_STATUS).includes(viewType)) return
        return dispatch(
            exportRegistrationsList({
                uuid: campaign.uuid,
                status: REGISTRATION_STATUS[viewType as keyof typeof REGISTRATION_STATUS],
            }),
        )
    }

    const handleOnOpenModal = (type: GLOBAL_MODAL_TYPE, selectRows: number): void => {
        dispatch(openModalWithProps({ type, count: selectRows }))
    }

    const handleClickApprove = (apiRef: React.MutableRefObject<GridApi>): void => {
        if (campaignUuid) {
            const rows = apiRef.current.getSelectedRows()
            const arrayRows = Array.from(rows)
            if (interestRegistrationRemainingSlots == null || arrayRows.length <= interestRegistrationRemainingSlots) {
                const massRegistrationUuids = arrayRows.map(([, item]) => item.uuid)
                const ids = arrayRows.map(([, item]) => item.id)
                dispatch(
                    massRegistration({
                        campaignUuid: campaignUuid,
                        uuids: massRegistrationUuids,
                        action_name: MASS_REGISTRATION_ACTION_NAME_ENUM.APPROVE,
                        getRegistrationListPayload: {
                            status: REGISTRATION_STATUS_ENUM.PENDING,
                            offset: activePageNum * MAX_PAGE_SIZE,
                            keywords: searchTerm,
                            ordering: sortQuery ? sortQuery : undefined,
                            filteringParams: memoizedFilteringParams,
                        },
                    }),
                )
                apiRef.current.selectRows(ids, false)
            } else {
                handleOnOpenModal(GLOBAL_MODAL_TYPE.INTEREST_REGISTRATION_MASS_APPROVAL_EXCEED, arrayRows.length)
            }
        }
    }

    const handleClickMoveToWaitlist = (apiRef: React.MutableRefObject<GridApi>): void => {
        if (campaignUuid) {
            const rows = apiRef.current.getSelectedRows()
            const arrayRows = Array.from(rows)
            const massRegistrationUuids = arrayRows.map(([, item]) => item.uuid)
            const ids = arrayRows.map(([, item]) => item.id)
            dispatch(
                massRegistration({
                    campaignUuid: campaignUuid,
                    uuids: massRegistrationUuids,
                    action_name: MASS_REGISTRATION_ACTION_NAME_ENUM.WAITLIST,
                    getRegistrationListPayload: {
                        status: REGISTRATION_STATUS_ENUM.PENDING,
                        offset: activePageNum * MAX_PAGE_SIZE,
                        keywords: searchTerm,
                        ordering: sortQuery ? sortQuery : undefined,
                        filteringParams: memoizedFilteringParams,
                    },
                }),
            )
            apiRef.current.selectRows(ids, false)
        }
    }

    const handleMoveToPending = (apiRef: React.MutableRefObject<GridApi>): void => {
        let action_name = MASS_REGISTRATION_ACTION_NAME_ENUM.REVERT_USER_ACCEPTED_TO_PENDING
        let status = REGISTRATION_STATUS_ENUM.ACCEPTED_INVITES
        switch (viewType) {
            case REGISTRATION_VIEW_ENUM.ACCEPTED_INVITES: {
                action_name = MASS_REGISTRATION_ACTION_NAME_ENUM.REVERT_USER_ACCEPTED_TO_PENDING
                status = REGISTRATION_STATUS_ENUM.ACCEPTED_INVITES
                break
            }
            case REGISTRATION_VIEW_ENUM.WAITLISTED: {
                action_name = MASS_REGISTRATION_ACTION_NAME_ENUM.REVERT_WAITLISTED_TO_PENDING
                status = REGISTRATION_STATUS_ENUM.WAITLISTED
                break
            }
            case REGISTRATION_VIEW_ENUM.AWAITING_RESPONSE: {
                action_name = MASS_REGISTRATION_ACTION_NAME_ENUM.REVERT_APPROVED_TO_PENDING
                status = REGISTRATION_STATUS_ENUM.AWAITING_RESPONSE
            }
        }
        if (campaignUuid) {
            const rows = apiRef.current.getSelectedRows()
            const arrayRows = Array.from(rows)
            const massRegistrationUuids = arrayRows.map(([, item]) => item.uuid)
            const ids = arrayRows.map(([, item]) => item.id)
            dispatch(
                massRegistration({
                    campaignUuid: campaignUuid,
                    uuids: massRegistrationUuids,
                    action_name: action_name,
                    dataType: 'registration',
                    getRegistrationListPayload: {
                        status,
                        offset: activePageNum * MAX_PAGE_SIZE,
                        keywords: searchTerm,
                        ordering: sortQuery ? sortQuery : undefined,
                        filteringParams: memoizedFilteringParams,
                    },
                }),
            )
            apiRef.current.selectRows(ids, false)
        }
    }

    const handleClickMoveToPrioriyList = (apiRef: React.MutableRefObject<GridApi>): void => {
        if (campaignUuid) {
            const rows = apiRef.current.getSelectedRows()
            const arrayRows = Array.from(rows)

            const massRegistrationUuids = arrayRows.map(([, item]) => item.uuid)
            const ids = arrayRows.map(([, item]) => item.id)
            dispatch(
                massRegistration({
                    campaignUuid: campaignUuid,
                    uuids: massRegistrationUuids,
                    action_name: MASS_REGISTRATION_ACTION_NAME_ENUM.GIVE_PRIORITY,
                    getRegistrationListPayload: {
                        status: REGISTRATION_STATUS_ENUM.WAITLISTED,
                        filteringParams: memoizedFilteringParams,
                        offset: activePageNum * MAX_PAGE_SIZE,
                        keywords: searchTerm,
                        ordering: sortQuery ? sortQuery : undefined,
                    },
                }),
            )
            apiRef.current.selectRows(ids, false)
        }
    }

    const onOpenActionDetailModal = (actionProps: ActionModalProps): void => {
        dispatch(openModalWithProps({ type: GLOBAL_MODAL_TYPE.INTEREST_REGISTRATION_ACTION_DETAIL, actionProps }))
    }
    const reorderColumns = (columns: GridColDef[]) => {
        const photoColumns = columns.filter((column: GridColDef) => column.field.includes(BLOCK_TYPE.PHOTO_SUBMISSION))

        if (photoColumns.length > 0) {
            const returnColumns = columns.filter(
                (column: GridColDef) => !column.field.includes(BLOCK_TYPE.PHOTO_SUBMISSION),
            )
            const nameBlockIndex = returnColumns.findIndex((column: GridColDef) => column.field === 'socialMediaInfo')
            returnColumns.splice(nameBlockIndex + 1, 0, ...photoColumns)
            return returnColumns
        } else {
            return columns
        }
    }

    const followerRequirementsPlatform = useMemo(() => getCampaignFollowerRequirements(campaign), [campaign])

    if (!campaign) return null

    switch (viewType) {
        case REGISTRATION_VIEW_ENUM.DROPOUTS:
        case SUBMISSION_VIEW_ENUM.DROPOUTS:
            if (campaignUuid) {
                const dropoutColumns: GridColDef[] = getDropoutColumns(
                    searchablePlatforms,
                    canManageTag,
                    followerRequirementsPlatform,
                )
                const dropoutRows: DropoutsTableRows[] = getDropoutRows(campaignUuid, submissions, profiles)
                return (
                    <div>
                        <PartiTable
                            title={TABLE_TITLE[viewType]}
                            rowHeight={84}
                            getRowHeight={() => 'auto'}
                            columns={dropoutColumns}
                            rows={dropoutRows}
                            isLoading={dropoutsLoading}
                            hideToolbar={false}
                            enableExport={dropoutRows.length > 0}
                            isNewStyle
                        />
                        <ExportModal
                            tableTitle={TABLE_TITLE[viewType]}
                            isRequesting={isSubmissionExportRequesting}
                            onClickExport={() => handleClickExport(viewType)}
                        />
                    </div>
                )
            }
            return null
        case REGISTRATION_VIEW_ENUM.REJECTED:
        case SUBMISSION_VIEW_ENUM.REJECTED:
            if (campaignUuid) {
                const rejectedColumns: GridColDef[] = getRejectedColumns(
                    viewType,
                    searchablePlatforms,
                    canManageTag,
                    followerRequirementsPlatform,
                )
                const rejectedRows: RejectedTableRows[] = getRejectedRows(campaignUuid, submissions, profiles, campaign)
                return (
                    <div>
                        <PartiTable
                            title={TABLE_TITLE[viewType]}
                            rowHeight={84}
                            getRowHeight={() => 'auto'}
                            columns={rejectedColumns}
                            rows={rejectedRows}
                            isLoading={rejectedLoading}
                            hideToolbar={false}
                            enableExport={rejectedRows.length > 0}
                            isNewStyle
                        />
                        <ExportModal
                            tableTitle={TABLE_TITLE[viewType]}
                            isRequesting={isSubmissionExportRequesting}
                            onClickExport={() => handleClickExport(viewType)}
                        />
                    </div>
                )
            }
            return null
        // todo: this view is not displayed yet, need to be checked and updated before using it
        case SUBMISSION_VIEW_ENUM.COMPLETED:
            if (campaign) {
                const title = campaign.title + ' - ' + newT('statusLabel.completed', 'Completed')
                const completedColumns: GridColDef[] = getCompletedColumns(canManageTag)
                const completedRows: CompletedTimeTableRows[] = getCompletedRows(
                    campaign.uuid,
                    submissions,
                    profiles,
                    campaign.country,
                )
                return (
                    <div>
                        <PartiTable
                            //exportData={completedRows} // todo: refactor this after api supoprt return all data
                            title={title}
                            rowHeight={84}
                            getRowHeight={() => 'auto'}
                            columns={completedColumns}
                            rows={completedRows}
                            isLoading={submissionLoading}
                            isNewStyle
                            showMoreAction
                        />
                    </div>
                )
            }
            return null
        case REGISTRATION_VIEW_ENUM.PENDING:
        case REGISTRATION_VIEW_ENUM.ACCEPTED_INVITES:
        case REGISTRATION_VIEW_ENUM.AWAITING_RESPONSE:
        case REGISTRATION_VIEW_ENUM.JOINED_CAMPAIGN:
            if (campaignUuid) {
                let registrationResultsColumns: GridColDef[] = getRegistrationResultsColumns(
                    REGISTRATION_STATUS[viewType],
                    dynamicCols,
                    campaign,
                    canManageTag,
                    searchablePlatforms,
                    followerRequirementsPlatform,
                    updateViewedRow,
                    handleHighlightRegistration,
                )
                const registrationResultsRows: RegistrationResultTableRows[] = getRegistrationResultsRows(
                    REGISTRATION_STATUS[viewType],
                    registrations,
                    profiles,
                    registrationFormBlocksById,
                    campaign,
                    occupationOptions,
                    viewedRows,
                    highlightedRegistrations,
                )
                registrationResultsColumns = reorderColumns(registrationResultsColumns)

                let returnReviewActionProps = {}
                const isListEmpty = registrationResultsRows.length === 0
                const isPendingList = viewType === REGISTRATION_VIEW_ENUM.PENDING && !isSelectedCampaignDeadlineOver
                const isAcceptedOrAwaitingList = [
                    REGISTRATION_VIEW_ENUM.ACCEPTED_INVITES,
                    REGISTRATION_VIEW_ENUM.AWAITING_RESPONSE,
                ].includes(viewType)

                if (isPendingList) {
                    returnReviewActionProps = {
                        enableMultiSelect: canApproveRegistrationAndManageWaitlist,
                        enableImportList: canApproveRegistrationAndManageWaitlist && !isListEmpty,
                        onClickRegistrationToolbarApprove: handleClickApprove,
                        onClickToolbarMoveToWaitlist: handleClickMoveToWaitlist,
                        remainingSlot: interestRegistrationRemainingSlots,
                    }
                } else if (canMoveRegistrationToPending && isAcceptedOrAwaitingList) {
                    returnReviewActionProps = {
                        enableMultiSelect: canMoveRegistrationToPending,
                        onClickMoveBackToPending: handleMoveToPending,
                        toolBarType: 'moveBackToPending',
                    }
                } else {
                    returnReviewActionProps = {
                        enableMultiSelect: false,
                    }
                }
                return (
                    <div>
                        <PartiTable
                            title={TABLE_TITLE[viewType]}
                            rowHeight={92}
                            getRowHeight={() => 'auto'}
                            columns={registrationResultsColumns}
                            rows={registrationResultsRows}
                            isLoading={registrationResultsLoading}
                            hideToolbar={false}
                            enableExport={!isListEmpty}
                            showFilter={true}
                            showSort={true}
                            isNewStyle
                            showMoreAction
                            tableHeight={tableHeight}
                            {...returnReviewActionProps}
                        />
                        <ExportModal
                            tableTitle={TABLE_TITLE[viewType]}
                            isRequesting={isRegistrationExportRequesting}
                            onClickExport={() => handleClickExport(viewType)}
                        />
                        <ListApprovalModal refreshList={refreshInterestRegistrationList} />
                    </div>
                )
            }
        case REGISTRATION_VIEW_ENUM.WAITLISTED:
            if (campaignUuid) {
                const waitlistColumns: GridColDef[] = getWaitlistColumns(
                    campaign,
                    searchablePlatforms,
                    followerRequirementsPlatform,
                )
                const waitlistRows: WaitListTableRows[] = getWaitlistRows(
                    campaign,
                    tasks,
                    registrations,
                    profiles,
                    registrationFormBlocksById,
                    onOpenActionDetailModal,
                )
                const reviewActionProps: Partial<PartiTableProps> = !isSelectedCampaignDeadlineOver
                    ? {
                          enableMultiSelect: canApproveRegistrationAndManageWaitlist || canMoveRegistrationToPending,
                          hideToolbar: false,
                          onClickMoveBackToPending: canMoveRegistrationToPending ? handleMoveToPending : null,
                          onClickMoveToPrioriyList: canApproveRegistrationAndManageWaitlist
                              ? handleClickMoveToPrioriyList
                              : null,
                          hideApproveOrderButton: !canApproveRegistrationAndManageWaitlist,
                      }
                    : {}
                return (
                    <>
                        <PartiTable
                            title={TABLE_TITLE[viewType]}
                            rowHeight={92}
                            getRowHeight={() => 'auto'}
                            columns={waitlistColumns}
                            rows={waitlistRows}
                            isLoading={registrationResultsLoading}
                            enableExport={false}
                            showFilter={true}
                            showSort={true}
                            hideTableBorder={true}
                            tableHeight={'980px'}
                            toolBarType={'waitlist'}
                            isNewStyle
                            onClickMoveToPrioriyList={handleClickMoveToPrioriyList}
                            {...reviewActionProps}
                        />
                        <ActionModal />
                    </>
                )
            }
        case REGISTRATION_VIEW_ENUM.DECLINED_INVITES:
            if (campaignUuid) {
                const declineRegistrationResultsColumns: GridColDef[] = getDeclinedRegistrationResultsColumns(
                    REGISTRATION_STATUS[viewType],
                    campaign,
                    searchablePlatforms,
                    canManageTag,
                    followerRequirementsPlatform,
                )
                const declineRegistrationResultsRows: DeclinedRegistrationResultsTableRows[] =
                    getDeclinedRegistrationResultsRows(REGISTRATION_STATUS[viewType], registrations, profiles, campaign)
                return (
                    <div>
                        <PartiTable
                            title={TABLE_TITLE[viewType]}
                            rowHeight={92}
                            getRowHeight={() => 'auto'}
                            columns={declineRegistrationResultsColumns}
                            rows={declineRegistrationResultsRows}
                            isLoading={registrationResultsLoading}
                            hideToolbar={false}
                            enableExport={declineRegistrationResultsRows.length > 0}
                            showFilter={true}
                            showSort={true}
                            isNewStyle
                            showMoreAction
                            tableHeight={tableHeight}
                        />
                        <ExportModal
                            tableTitle={TABLE_TITLE[viewType]}
                            isRequesting={isRegistrationExportRequesting}
                            onClickExport={() => handleClickExport(viewType)}
                        />
                    </div>
                )
            }
            return null
        default:
            return null
    }
}

export default SimpleTableWrapper
